import React, { useEffect, useState, useContext, useRef } from "react";
import { useForm } from "react-hook-form";
import { Col, Container, Image, Row, Button, Modal } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { faTimes } from "@fortawesome/fontawesome-free-solid";
import { Entry } from "../../redux/entrySlice";
import MultipleFileUpload from "../../components/MultipleFileUpload/MultipleFileUpload";
import TimePickerComponent from "../../components/TimePicker/TimePicker";
import CustomTable from "../../components/CustomTable/CustomTable";
import TextInput from "../../components/TextField/TextField";
import EmailInput from "../../components/EmailBox/EmailBox";
import SelectBox from "../../components/SelectBox/SelectBox";

import {
  REACT_APP_API_FETCH_MENUS,
  SERVER_URL,
} from "../../redux/apiConstants";
import styles from "./editCafePopUp.module.scss";
import "react-toastify/dist/ReactToastify.css";
import { faEdit } from "@fortawesome/fontawesome-free-solid";
import { TableContext } from "../../contexts/tableContext";
import { useNavigate, useParams } from "react-router-dom";
import { getCookies, getPermissions } from "../../hooks/useCookies";
import { confirmAlert } from "react-confirm-alert";
import LocationSelectBox from "../../components/LocationSelectBox/LocationSelectBox";
import MapPinPickerModal from "../../components/MapPinPickerModal/MapPinPickerModal";
import axios from "axios";
import envValues from "../../enviornment";
import NumberInput from "../../components/NumberBox/NumberBox";
import useToggle from "../../hooks/useToggle";
import RefreshPosItemsModal from "../../components/RefreshPosItemsModal/RefreshPosItemsModal";
import { Badge } from "react-bootstrap";
import { DateFormatter } from "../../utilityFunctions/utilsFunctions";
import ViewVendorPopup from "../vendors/viewVendorPopup";
import Loader from '../../components/Loader/Loader';
import DrinkIdentityTagAssigner from "../../components/DrinkIdentityTagAssigner";
import ditStyles from "../drinkIdentityTags/index.module.scss";
import VendorDocumentsSection from "./VendorDocumentsSection";
import OrderAheadEnabledBadge, {
  isCafeOrderAheadEnabled,
} from "./OrderAheadEnabledBadge";
import CafeNameAvatar, { isUsableCafeImageUrl } from "./CafeNameAvatar";

function SuspendCafeModal({ show, handleClose, cafeId }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const periods = [
    { value: "1week", label: "1 Week" },
    { value: "1month", label: "1 Month" },
    { value: "6month", label: "6 Month" },
  ];

  const { setSuspend } = useContext(TableContext);
  const dispatch = useDispatch();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      reason: "",
      period: "",
    },
  });

  const handleSuspendCafe = (data) => {
    setSubmitting(true);
    setError(null);

    if (!data.reason || !data.period) {
      setError("Please provide both reason and suspension period");
      setSubmitting(false);
      return;
    }

    const apiData = {
      suspendReason: data.reason,
      suspendPeriod: data.period,
    };

    const actionData = {
      actionUrl: `cafe/suspend-cafe/${cafeId}`,
      actionMethod: "post",
      apiData: apiData,
    };

    dispatch(Entry(actionData))
      .then((resp) => {
        setSubmitting(false);
        if (resp.payload.code === 200) {
          toast.success("Cafe suspended successfully");
          setSuspend(true);
          handleClose();
          reset(); // Reset form after successful submission
        } else {
          setError(resp.payload.message || "Error suspending cafe");
          toast.error("Failed to suspend cafe");
        }
      })
      .catch((error) => {
        setSubmitting(false);
        setError("Network error. Please try again.");
        toast.error("Network error suspending cafe");
        console.error("Suspend Cafe Error:", error);
      });
  };

  const handleClickSuspend = () => {
    handleSubmit(handleSuspendCafe)(); // Trigger the form validation and submission
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Suspend Cafe</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form>
          <TextInput
            className={styles.inputbox}
            labelstyle={styles.formlabel}
            label="Reason"
            fieldName="reason"
            placeHolder="Enter reason for suspension"
            register={register}
            errors={errors}
            isRequired
            mandatory
          />
          <SelectBox
            className={styles.inputbox}
            name="period"
            label="Suspension Period"
            labelstyle={styles.formlabel}
            control={control}
            register={register}
            values={periods}
            errors={errors}
            placeholder="Select Suspension Period"
            mandatory
            isRequired
          />
          <div className="d-flex justify-content-between mt-3">
            <Button
              color="primary"
              onClick={handleClickSuspend}
              disabled={submitting}
            >
              {submitting ? "Suspending..." : "Suspend Cafe"}
            </Button>
            <Button
              color="secondary"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
          </div>
          {error && <div className="text-danger mt-2">{error}</div>}
        </form>
      </Modal.Body>
    </Modal>
  );
}

const addonPriceError = (price, orderAheadEnabled) => {
  const priceText = String(price ?? "").trim();
  if (!priceText) {
    return orderAheadEnabled ? "Price is required" : "";
  }
  const amount = Number(priceText);
  if (Number.isNaN(amount)) {
    return "Invalid price";
  }
  if (orderAheadEnabled && amount <= 0) {
    return "Price must be greater than 0";
  }
  if (!orderAheadEnabled && amount < 0) {
    return "Price must be 0 or above";
  }
  return "";
};

const normalizeAddonPrice = (price) => {
  const priceText = String(price ?? "").trim();
  return priceText === "" ? null : Number(priceText);
};

const AddOnModal = ({
  show,
  handleClose,
  cafeId,
  fetchAddons,
  orderAheadEnabled,
}) => {
  const [addonSet, setAddonSet] = useState({
    name: "",
    addOns: [{ name: "", price: "" }],
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  // Handle changes in addOn fields
  const handleAddonChange = (index, field, value) => {
    const updatedAddOns = [...addonSet.addOns];
    updatedAddOns[index][field] = value;

    // Clear error message on typing
    setErrors((prev) => ({ ...prev, [`addon-${index}-${field}`]: "" }));

    setAddonSet({ ...addonSet, addOns: updatedAddOns });
  };

  // Add new addon field
  const addNewAddon = () => {
    setAddonSet({
      ...addonSet,
      addOns: [...addonSet.addOns, { name: "", price: "" }],
    });
  };

  // Remove addon field (prevent deleting all)
  const removeAddon = (index) => {
    if (addonSet.addOns.length > 1) {
      const updatedAddOns = addonSet.addOns.filter((_, i) => i !== index);
      setAddonSet({ ...addonSet, addOns: updatedAddOns });
    }
  };

  // Validate input fields
  const validate = () => {
    let tempErrors = {};
    if (!addonSet.name.trim()) {
      tempErrors.name = "Addon Set Name is required";
    }

    addonSet.addOns.forEach((addon, index) => {
      if (!addon.name.trim()) {
        tempErrors[`addon-${index}-name`] = "Addon name is required";
      }
      const priceError = addonPriceError(addon.price, orderAheadEnabled);
      if (priceError) {
        tempErrors[`addon-${index}-price`] = priceError;
      }
    });

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  // Save function
  const onSave = async () => {
    if (validate()) {
      setLoading(true);
      // Perform save operation (API call, etc.)
      console.log("Saving Addons:", addonSet);

      const actionData = {
        actionUrl: `cafe/add-addons/${cafeId}`,
        actionMethod: "post",
        apiData: {
          ...addonSet,
          addOns: addonSet.addOns.map((addon) => ({
            ...addon,
            price: normalizeAddonPrice(addon.price),
          })),
        },
      };

      dispatch(Entry(actionData))
        .then((resp) => {
          setLoading(false);
          if (resp.payload.code === 200) {
            toast.success("Addon created successfully");
            setAddonSet({
              name: "",
              addOns: [{ name: "", price: "" }],
            });
            if (fetchAddons) {
              fetchAddons();
            }
            handleClose();
          } else {
            toast.error("Failed to create addon");
          }
        })
        .catch((error) => {
          console.error("Suspend Cafe Error:", error);
        });
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Add New Addon Set</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ padding: "20px" }}>
          {/* Addon Set Name */}
          <input
            type="text"
            placeholder="Addon Set Name"
            value={addonSet.name}
            onChange={(e) => {
              setAddonSet({ ...addonSet, name: e.target.value });
              setErrors((prev) => ({ ...prev, name: "" }));
            }}
            style={{
              width: "100%",
              padding: "8px",
              marginBottom: "5px",
              border: "1px solid #ccc",
              borderRadius: "5px",
            }}
          />
          {errors.name && (
            <div
              style={{ color: "red", fontSize: "12px", marginBottom: "10px" }}
            >
              {errors.name}
            </div>
          )}

          <h5>Addons:</h5>
          {addonSet.addOns.map((addon, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                gap: "10px",
                marginBottom: "10px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {/* Addon Name Input */}
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  placeholder="Addon Name"
                  value={addon.name}
                  onChange={(e) =>
                    handleAddonChange(index, "name", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                  }}
                />
                {errors[`addon-${index}-name`] && (
                  <div style={{ color: "red", fontSize: "12px" }}>
                    {errors[`addon-${index}-name`]}
                  </div>
                )}
              </div>

              {/* Addon Price Input */}
              <div style={{ flex: 1 }}>
                <input
                  type="number"
                  placeholder={orderAheadEnabled ? "Price" : "Price (optional)"}
                  value={addon.price}
                  onChange={(e) =>
                    handleAddonChange(index, "price", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                  }}
                />
                {errors[`addon-${index}-price`] && (
                  <div style={{ color: "red", fontSize: "12px" }}>
                    {errors[`addon-${index}-price`]}
                  </div>
                )}
              </div>

              {/* Remove Button (Disabled if only one addon remains) */}
              <button
                onClick={() => removeAddon(index)}
                disabled={addonSet.addOns.length === 1}
                style={{
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  border: "none",
                  padding: "5px 10px",
                  cursor:
                    addonSet.addOns.length === 1 ? "not-allowed" : "pointer",
                  borderRadius: "5px",
                  opacity: addonSet.addOns.length === 1 ? 0.6 : 1,
                }}
              >
                X
              </button>
            </div>
          ))}

          {/* Add More Button */}
          <button
            onClick={addNewAddon}
            style={{
              backgroundColor: "#007bff",
              color: "#fff",
              border: "none",
              padding: "8px 12px",
              cursor: "pointer",
              borderRadius: "5px",
              marginTop: "10px",
              width: "100%",
            }}
          >
            + Add More
          </button>

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "20px",
            }}
          >
            <button
              onClick={onSave}
              disabled={loading}
              style={{
                backgroundColor: "#007bff",
                color: "#fff",
                border: "none",
                padding: "8px 12px",
                cursor: "pointer",
                borderRadius: "5px",
                flex: 1,
                marginRight: "5px",
              }}
            >
              {loading ? "Saving..." : "Save"}
            </button>
            <button
              onClick={handleClose}
              style={{
                backgroundColor: "#dc3545",
                color: "#fff",
                border: "none",
                padding: "8px 12px",
                cursor: "pointer",
                borderRadius: "5px",
                flex: 1,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const OptionsModal = ({ show, handleClose, cafeId, fetchOptions }) => {
  const [optionsSet, setOptionsSet] = useState({
    name: "",
    values: [{ name: "" }],
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  // Handle changes in options values
  const handleOptionChange = (index, field, value) => {
    const updatedValues = [...optionsSet.values];
    updatedValues[index][field] = value;

    setErrors((prev) => ({ ...prev, [`value-${index}-${field}`]: "" }));

    setOptionsSet({ ...optionsSet, values: updatedValues });
  };

  // Add new option value
  const addNewValue = () => {
    setOptionsSet({
      ...optionsSet,
      values: [...optionsSet.values, { name: "" }],
    });
  };

  // Remove option value (prevent deleting all)
  const removeValue = (index) => {
    if (optionsSet.values.length > 1) {
      const updatedValues = optionsSet.values.filter((_, i) => i !== index);
      setOptionsSet({ ...optionsSet, values: updatedValues });
    }
  };

  // Validate input fields
  const validate = () => {
    let tempErrors = {};
    if (!optionsSet.name.trim()) {
      tempErrors.name = "Option Set Name is required";
    }

    optionsSet.values.forEach((value, index) => {
      if (!value.name.trim()) {
        tempErrors[`value-${index}-name`] = "Value name is required";
      }
    });

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  // Save function
  const onSave = async () => {
    if (validate()) {
      setLoading(true);

      const actionData = {
        actionUrl: `cafe/add-options/${cafeId}`,
        actionMethod: "post",
        apiData: optionsSet,
      };

      dispatch(Entry(actionData))
        .then((resp) => {
          setLoading(false);
          if (resp.payload.code === 200) {
            toast.success("Option set created successfully");
            setOptionsSet({
              name: "",
              values: [{ name: "" }],
            });

            fetchOptions();

            handleClose();
          } else {
            toast.error("Failed to create option set");
          }
        })
        .catch((error) => {
          console.error("Option Set Error:", error);
        });
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Add New Option Set</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ padding: "20px" }}>
          {/* Option Set Name */}
          <input
            type="text"
            placeholder="Option Set Name"
            value={optionsSet.name}
            onChange={(e) => {
              setOptionsSet({ ...optionsSet, name: e.target.value });
              setErrors((prev) => ({ ...prev, name: "" }));
            }}
            style={{
              width: "100%",
              padding: "8px",
              marginBottom: "5px",
              border: "1px solid #ccc",
              borderRadius: "5px",
            }}
          />
          {errors.name && (
            <div
              style={{ color: "red", fontSize: "12px", marginBottom: "10px" }}
            >
              {errors.name}
            </div>
          )}

          <h5>Values:</h5>
          {optionsSet.values.map((value, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                gap: "10px",
                marginBottom: "10px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  placeholder="Value Name"
                  value={value.name}
                  onChange={(e) =>
                    handleOptionChange(index, "name", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                  }}
                />
                {errors[`value-${index}-name`] && (
                  <div style={{ color: "red", fontSize: "12px" }}>
                    {errors[`value-${index}-name`]}
                  </div>
                )}
              </div>
              <button
                onClick={() => removeValue(index)}
                disabled={optionsSet.values.length === 1}
                style={{
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  border: "none",
                  padding: "5px 10px",
                  cursor:
                    optionsSet.values.length === 1 ? "not-allowed" : "pointer",
                  borderRadius: "5px",
                  opacity: optionsSet.values.length === 1 ? 0.6 : 1,
                }}
              >
                X
              </button>
            </div>
          ))}

          {/* Add More Button */}
          <button
            onClick={addNewValue}
            style={{
              backgroundColor: "#007bff",
              color: "#fff",
              border: "none",
              padding: "8px 12px",
              cursor: "pointer",
              borderRadius: "5px",
              marginTop: "10px",
              width: "100%",
            }}
          >
            + Add More
          </button>

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "20px",
            }}
          >
            <button
              onClick={onSave}
              disabled={loading}
              style={{
                backgroundColor: "#007bff",
                color: "#fff",
                border: "none",
                padding: "8px 12px",
                cursor: "pointer",
                borderRadius: "5px",
                flex: 1,
                marginRight: "5px",
              }}
            >
              {loading ? "Saving..." : "Save"}
            </button>
            <button
              onClick={handleClose}
              style={{
                backgroundColor: "#dc3545",
                color: "#fff",
                border: "none",
                padding: "8px 12px",
                cursor: "pointer",
                borderRadius: "5px",
                flex: 1,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const EditCafePopup = React.memo(
  ({
    cafeId,
    setTableData,
    handleClose,
    initialAction = "view",
    setIsUpdated,
    edit = true,
  }) => {
    const { suspend, setSuspend } = useContext(TableContext);
    const params = useParams();
    // let payoutId;
    const token = `Bearer ${getCookies("Token")}`;
    const [details, setDetails] = useState([]);
    const { setDashUpdate } = useContext(TableContext);
    const dateFormat = getCookies("dateFormat");
    const [canDelete, setCanDelete] = useState(true);
    const [cafeList, setCafesList] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showAddOnModal, setShowAddOnModal] = useState(false);
    const [showOptionsModal, setShowOptionsModal] = useState(false);
    const [showRefreshModal, setShowRefreshModal] = useState(false);
    const [showVendorPopup, setShowVendorPopup] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [cafe, setCafe] = useState(null);
    const [error, setError] = useState(null);
    const [action, setAction] = useState(initialAction);
    const [isEditMode, setIsEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [duplicateLoading, setDuplicateLoading] = useState(false);

    const { files } = useContext(TableContext);
    const [image, setImage] = useState(null);
    const [selectedImageFile, setSelectedImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [cafeImageLoadFailed, setCafeImageLoadFailed] = useState(false);
    const imageInputRef = useRef(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const textareaRef = useRef(null);
    const [text, setText] = useState();

    // Reset image selection states when closing or cancelling
    const resetImageSelection = () => {
      setSelectedImageFile(null);
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
      setImagePreview(null);
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
    };

    // Wrapper for handleClose that also resets image state
    const handleCloseWithReset = () => {
      resetImageSelection();
      handleClose();
    };
    useEffect(() => {
      setCafeImageLoadFailed(false);
    }, [cafe?._id, image, imagePreview]);

    useEffect(() => {
      console.log("image", selectedImageFile);
    }, [selectedImageFile]);
    const [locations, setLocations] = useState([]);
    const [locationDefault, setLocationDefault] = useState([]);
    const [claimRequestCount, setClaimCount] = useState(0);
    const [timeInvalid, setTimeInvalid] = useState(false);
    const [addOnSets, setAddOnSets] = useState([]);
    const [optionsSets, setOptionsSets] = useState([]);
    const [commissionValue, setCommissionValue] = useState();
    const [uptickAmountStatus, setUptickAmountStatus] = useState();
    const [uptickAmountType, setUptickAmountType] = useState();
    const [canApprove, setCanApprove] = useState(false);
    const [canEditVendorApplication, setCanEditVendorApplication] = useState(false);

    const [selectedCafeId, setSelectedCafeId] = useState();
    const [showTablePopUp, setShowTablePopUp] = useToggle();

    let defaultLocation;
    const [timings, setTimings] = useState([
      { dayOfWeek: "Monday", periods: [] },
      { dayOfWeek: "Tuesday", periods: [] },
      { dayOfWeek: "Wednesday", periods: [] },
      { dayOfWeek: "Thursday", periods: [] },
      { dayOfWeek: "Friday", periods: [] },
      { dayOfWeek: "Saturday", periods: [] },
      { dayOfWeek: "Sunday", periods: [] },
    ]);

    // Helper function to generate unique ID for periods
    const generatePeriodId = () => `period_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const [locationLoading, setLocationLoading] = useState(false);
    const [manualCoordinates, setManualCoordinates] = useState(null);
    const [showMapModal, setShowMapModal] = useState(false);
    const [selectedLocationData, setSelectedLocationData] = useState(null);

    const getLocation = async (text) => {
      if (!text || text.length < 3) return;

      try {
        setLocationLoading(true);
        const response = await axios.get(
          `${SERVER_URL}/location?location=${text}`
        );
        console.log("location-----", response.data);
        setLocations(response.data.data);
      } catch (error) {
        console.log("erroorrr--", error);
        console.error("Error fetching location:", error);
      } finally {
        setLocationLoading(false);
      }
    };

    useEffect(() => {
      if (text?.length > 0) getLocation(text);
      //   setLocationDefault([{
      //     formatted_address: locations[0].geometry.location,
      //     location: locations[0].geometry.location,
      // }])
    }, [text]);
    console.log(text, "text");

    const handleCloseModal = () => {
      setShowModal(false);
    };

    const handleCloseAddOnModal = () => {
      setShowAddOnModal(false);
    };

    const handleCloseOptionsModal = () => {
      setShowOptionsModal(false);
    };

    const handleCloseRefreshModal = () => {
      setShowRefreshModal(false);
      // Refresh cafe data after sync
      const actionData = { actionUrl: `cafe/${cafeId}`, actionMethod: "get" };
      dispatch(Entry(actionData)).then((resp) => {
        if (resp.payload && resp.payload.data && resp.payload.data.cafe) {
          setCafe(resp.payload.data.cafe);
        }
      });
    };

    const MAX_IMAGE_SIZE = 3 * 1024 * 1024; // 3MB in bytes

    const handleImageClick = () => {
      if (isEditMode && imageInputRef.current) {
        imageInputRef.current.click();
      }
    };

    const handleImageChange = (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      // Validate file size (max 3MB)
      if (file.size > MAX_IMAGE_SIZE) {
        toast.error("Image size must be less than 3MB");
        event.target.value = ""; // Reset the input
        return;
      }
      console.log("file", file);
      setSelectedImageFile(file);

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    };

    // Cleanup preview URL on unmount or when preview changes
    useEffect(() => {
      return () => {
        if (imagePreview) {
          URL.revokeObjectURL(imagePreview);
        }
      };
    }, [imagePreview]);

    // Main form for cafe editing
    const {
      control,
      register,
      watch,
      handleSubmit,
      formState: { errors },
      reset,
      setValue,
    } = useForm();

    const watchLocationName = watch("location_name");

    const handleOpenMapModal = () => {
      // Allow opening map even without prior selection
      setShowMapModal(true);
    };

    const handleMapConfirm = (data) => {
      setManualCoordinates(data.coordinates);
      setSelectedLocationData({
        location: data.coordinates,
        formatted_address: data.address,
      });
      
      // Update the form value directly - this will show in the autocomplete field
      const locationData = {
        location: data.coordinates,
        formatted_address: data.address,
      };
      
      // Use setValue instead of reset to update just the location field
      setValue("location_name", locationData);
      
      setShowMapModal(false);
      toast.success("Location set from map!");
    };

    const handleResetManualPin = () => {
      setManualCoordinates(null);
      setSelectedLocationData(null);
      setValue("location_name", null);
      toast.info("Location cleared - please select again");
    };

    const handleApprove = async (id) => {
      // Check if application is rejected
      if (cafe?.applicationStatus && cafe.applicationStatus === "Rejected") {
        confirmAlert({
          title: "Application Rejected",
          message: "This café's application has been rejected. The vendor must resubmit their application with the required updates before it can be approved.",
          buttons: [
            {
              label: "View Application Details",
              onClick: () => {
                navigate(`/vendorapplications/viewdetails/${id}`);
              },
            },
            {
              label: "Close",
            },
          ],
        });
        return;
      }

      // If this cafe has an applicationStatus, show dialog to redirect to vendor applications
      if (cafe?.applicationStatus && (cafe.applicationStatus === "Submitted" || cafe.applicationStatus === "In Review")) {
        confirmAlert({
          title: "Review Application Documents",
          message: "Please review and approve all application documents first from the Vendor Applications section.",
          buttons: [
            {
              label: "Go to Application Details",
              onClick: () => {
                navigate(`/vendorapplications/viewdetails/${id}`);
              },
            },
            {
              label: "Cancel",
            },
          ],
        });
        return;
      }

      confirmAlert({
        title: canDelete ? "" : "Oops!!",
        message: "Are you sure you want to approve the cafe request ?",
        buttons: canDelete
          ? [
              {
                label: "Yes",
                onClick: () => {
                  const actionData = {};
                  actionData.actionUrl = `cafe/approve-cafe/${id}`;
                  actionData.actionMethod = "post";
                  dispatch(Entry(actionData))
                    .then(() => {
                      // navigate(-1);
                      handleClose();
                      setDashUpdate((prevDashUpdate) => !prevDashUpdate);
                      setIsUpdated(true);
                      setSuspend(false);
                      toast.success("Cafe request approved successfully");
                    })
                    .catch((err) => {
                      toast.error("Error approving cafe request");
                      console.error("Error: ", err);
                    });
                },
              },
              {
                label: "No",
                // onClick: () => alert("Click No")
              },
            ]
          : [
              {
                label: "Ok",
                // onClick: () => alert("Click No")
              },
            ],
      });
    };

    const handleLocationChange = async (value) => {
      setText(value);
    };

    const handleReject = async () => {
      confirmAlert({
        title: "Reject Cafe Application",
        customUI: ({ onClose }) => {
          return (
            <div className="react-confirm-alert-body">
              <h1>Reject Cafe Application</h1>
              <p>Please provide detailed feedback for the vendor:</p>
              <textarea
                id="rejectionNotesInput"
                className="form-control"
                rows={4}
                placeholder="Please provide detailed feedback for the vendor..."
                style={{ width: "100%", marginBottom: "15px" }}
              />
              <div className="react-confirm-alert-button-group">
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    const notes = document.getElementById("rejectionNotesInput").value;
                    if (!notes || notes.trim().length < 10) {
                      toast.warning("Please provide at least 10 characters of feedback");
                      return;
                    }
                    
                    const actionData = {
                      actionUrl: `cafe/reject-cafe/${cafeId}`,
                      actionMethod: "post",
                      apiData: { notes },
                    };
                    
                    dispatch(Entry(actionData))
                      .then(() => {
                        handleClose();
                        setIsUpdated(true);
                        toast.success("Cafe request rejected successfully");
                        onClose();
                      })
                      .catch((err) => {
                        toast.error("Error rejecting cafe request");
                        console.error("Error: ", err);
                      });
                  }}
                >
                  Confirm Rejection
                </button>
                <button className="btn btn-secondary" onClick={onClose}>
                  Cancel
                </button>
              </div>
            </div>
          );
        },
      });
    };

    const handleReviewDocument = (cafeId, documentType) => {
      confirmAlert({
        title: "Confirm Review",
        message: `Mark this ${documentType === 'healthPermit' ? 'Health Permit' : 'Insurance'} document as reviewed?`,
        buttons: [
          {
            label: "Yes, Mark as Reviewed",
            onClick: () => {
              const data = {
                actionUrl: `cafe/${cafeId}/review-document`,
                actionMethod: "post",
                apiData: { documentType },
              };
              dispatch(Entry(data)).then((resp) => {
                if (resp.payload.code === 200) {
                  toast.success("Document marked as reviewed!");
                  // Refresh cafe details
                  const refreshData = { actionUrl: `cafe/${cafeId}`, actionMethod: "get" };
                  dispatch(Entry(refreshData)).then((refreshResp) => {
                    if (refreshResp.payload && refreshResp.payload.data && refreshResp.payload.data.cafe) {
                      setCafe(refreshResp.payload.data.cafe);
                    }
                  });
                } else {
                  toast.error(resp.payload.message || "Failed to review document");
                }
              });
            },
          },
          {
            label: "Cancel",
            onClick: () => {},
          },
        ],
      });
    };

    const handleDelete = (id) => {
      confirmAlert({
        title: canDelete ? "" : "Oops!!",
        message: "Are you sure you want to delete?",
        buttons: canDelete
          ? [
              {
                label: "Yes",
                onClick: () => {
                  const actionData = {};
                  actionData.actionUrl = `cafe/${id}`;
                  actionData.actionMethod = "delete";
                  dispatch(Entry(actionData)).then(() => {
                    navigate("/cafe");
                    handleClose();
                    setIsUpdated(true);
                    toast.success("Cafe deleted successfully");
                    setTableData((prevData) =>
                      prevData.filter((item) => item.id !== id)
                    );
                  });
                },
              },
              {
                label: "No",
                // onClick: () => alert("Click No")
              },
            ]
          : [
              {
                label: "Ok",
                // onClick: () => alert("Click No")
              },
            ],
      });
    };

    const removeSuspension = (id) => {
      confirmAlert({
        title: canDelete ? "" : "Oops!!",
        message: "Are you sure you want to remove suspension?",
        buttons: canDelete
          ? [
              {
                label: "Yes",
                onClick: () => {
                  const actionData = {};
                  actionData.actionUrl = `cafe/un-suspend-cafe/${id}`;
                  actionData.actionMethod = "post";
                  dispatch(Entry(actionData)).then(() => {
                    // navigate(-1);
                    setSuspend(false);
                    toast.success("Cafe unsuspended successfully");
                  });
                },
              },
              {
                label: "No",
                // onClick: () => alert("Click No")
              },
            ]
          : [
              {
                label: "Ok",
                // onClick: () => alert("Click No")
              },
            ],
      });
    };

    function convertTo24Hour(time12) {
      const [time, period] = time12.split(" ");
      let [hours, minutes] = time.split(":").map(Number);
      if (period === "PM" && hours !== 12) {
        hours += 12;
      } else if (period === "AM" && hours === 12) {
        hours = 0;
      }

      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
    }

    // Helper function to parse existing location data
    const parseExistingLocation = (cafeData) => {
      if (!cafeData?.location_name) return null;

      try {
        let locationObj = null;
        
        // Check if location is already an object or needs parsing
        if (cafeData.location) {
          if (typeof cafeData.location === 'string') {
            locationObj = JSON.parse(cafeData.location);
          } else if (typeof cafeData.location === 'object') {
            // Check if it's GeoJSON format (MongoDB style)
            if (cafeData.location.type === 'Point' && Array.isArray(cafeData.location.coordinates)) {
              // GeoJSON uses [longitude, latitude] order
              locationObj = {
                lng: cafeData.location.coordinates[0],
                lat: cafeData.location.coordinates[1]
              };
            } else {
              // Already in Google Maps format {lat, lng}
              locationObj = cafeData.location;
            }
          }
        }
        
        return {
          formatted_address: cafeData.location_name,
          location: locationObj,
          place_id: cafeData.place_id || null,
        };
      } catch (error) {
        console.error("Error parsing location:", error);
        return {
          formatted_address: cafeData.location_name,
          location: null,
        };
      }
    };

    useEffect(() => {
      const actionData = { actionUrl: `cafe/${cafeId}`, actionMethod: "get" };
      setTimeInvalid(false);
      setLoading(true);
      dispatch(Entry(actionData)).then((resp) => {
        if (resp.payload && resp.payload.data && resp.payload.data.cafe) {
          const cafeData = resp.payload.data.cafe;
          const cafeTimings = cafeData.timings;
          
          // Convert timings to new format (handle both old and new format from API)
          const convertedTimings = cafeTimings.map((timing) => {
            // If already in new format with periods
            if (timing.periods && Array.isArray(timing.periods)) {
              return {
                dayOfWeek: timing.dayOfWeek,
                periods: timing.periods.map(p => ({
                  id: p.id || generatePeriodId(),
                  startLocalTime: p.startLocalTime || "",
                  endLocalTime: p.endLocalTime || "",
                  isNextDay: p.isNextDay || false
                }))
              };
            }
            // If in old format with startTime/endTime
            else {
              const { dayOfWeek, startTime, endTime, isNextDay } = timing;
              return {
                dayOfWeek,
                periods: [{
                  id: generatePeriodId(),
                  startLocalTime: startTime ? (startTime.includes("AM") || startTime.includes("PM") ? convertTo24Hour(startTime) : startTime) : "",
                  endLocalTime: endTime ? (endTime.includes("AM") || endTime.includes("PM") ? convertTo24Hour(endTime) : endTime) : "",
                  isNextDay: isNextDay || false
                }]
              };
            }
          });
          cafeData.timings = convertedTimings;
          console.log("cafe data", cafeData);
          setClaimCount(cafeData?.claimRequestsCount || 0);
          console.log("count", cafeData?.claimRequestsCount);
          setCafe(resp.payload.data.cafe);
          setCommissionValue(resp.payload.data.cafe?.commission || "Disable");
          setUptickAmountStatus(
            resp.payload.data.cafe?.uptickAmountStatus || "Disable"
          );
          setUptickAmountType(
            resp.payload.data.cafe?.uptickAmountType || "Percentage"
          );

          console.log("cafe", resp.payload.data.cafe);
          if (
            !resp.payload.data.cafe.image ||
            resp.payload.data.cafe.image.length === 0
          ) {
            setImage(null);
          } else {
            setImage(resp.payload.data.cafe.image);
          }
          setLoading(false);
          setSuspend(resp.payload.data.cafe.isSuspended);
          reset({
            ...resp.payload.data.cafe,
            locationTitle:
              resp.payload.data.cafe.locationTitle ||
              resp.payload.data.cafe.specificLocation ||
              "",
            phone: resp.payload.data.cafe.vendor?.phone,
            email:
              resp.payload.data.cafe.cafeStatus === "Unclaimed"
                ? resp.payload.data.cafe.email || ""
                : resp.payload.data.cafe.vendor?.email,
            location_name: parseExistingLocation(resp.payload.data.cafe),
            hasSpecialInstruction:
              resp.payload.data.cafe?.hasSpecialInstruction !== false,
              commissionMilestoneOverride:resp.payload.data.cafe?.commissionMilestoneOverride,
              commissionMilestone:
              Number(resp.payload.data.cafe?.commissionMilestone),
          });
          setText(resp.payload.data.cafe.location_name);
          getLocation(resp.payload.data.cafe.location_name);
          // setLocationDefault([{
          //     formatted_address: resp.payload.data.cafe.location_name,
          //     location: resp.payload.data.cafe.location,
          // }])

          const textarea = document.getElementById("description");
          if (textarea) {
            adjustTextareaHeight(textarea);
          }
          setTimings(cafeData.timings);
          const isAnyTimingFilled = cafeTimings.some(
            (timing) =>
              (timing.startTime && !timing.endTime) ||
              (!timing.startTime && timing.endTime)
          );
          console.log("first", isAnyTimingFilled);
          setTimeInvalid(isAnyTimingFilled);
        } else {
          console.error("Cafe data not found in response:", resp);
          setCafe(null);
        }
      });
    }, [cafeId, dispatch, reset]);

    function convertTo12Hour(time24) {
      const [hours, minutes] = time24.split(":").map(Number);
      if (time24.includes("AM") || time24.includes("PM")) {
        return time24;
      }
      const period = hours >= 12 ? "PM" : "AM";
      const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight
      return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
    }

    const onSubmitData = (data) => {
      console.log("data===", data);
      setSubmitting(true);
      setError(null);
      
      // Clean timings - remove the 'id' field before sending to API
      const cleanedTimings = timings.map(timing => ({
        dayOfWeek: timing.dayOfWeek,
        periods: timing.periods.map(period => ({
          startLocalTime: period.startLocalTime,
          endLocalTime: period.endLocalTime,
          isNextDay: period.isNextDay
        }))
      }));
      
      // Timings are already in the correct format with periods
      let apiData = {
        name: data.name,
        address: data.address,
        locationTitle: data.locationTitle,
        businessAddress: data.businessAddress,
        description: data.description ?? "",
        timings: cleanedTimings,
        facebook: data.facebook ?? "",
        instagram: data.instagram ?? "",
        commission: data.commission ?? "Disable",
        commissionMilestoneOverride: !!data.commissionMilestoneOverride,
        commissionMilestone: data.commissionMilestoneOverride
          ? data.commissionMilestone ?? 10
          : 0,
        commissionAmount: data.commissionAmount ?? 10,
        ccProcessingConfig: {
          percentage: data?.ccProcessingConfig?.percentage || "2.9",
          fixedFee: data?.ccProcessingConfig?.fixedFee || "0.3",
        },
        uptickAmountStatus: data?.uptickAmountStatus || "Disable",
        uptickAmountType: data?.uptickAmountType || "Percentage",
        uptickAmount: data?.uptickAmount || 0,
        hasSpecialInstruction: !!data?.hasSpecialInstruction,
        File: files,
      };

      if (cafe?.cafeStatus === "Unclaimed") {
        apiData.email = data.email ?? "";
      }

      // Include new cafe image if selected
      if (selectedImageFile) {
        console.log("selectedImageFile---", selectedImageFile);
        apiData.image = selectedImageFile;
      }

      // Use manual coordinates if set, otherwise use form data or existing cafe location
      if (manualCoordinates) {
        apiData.location = JSON.stringify(manualCoordinates);
        apiData.location_name = data.location_name?.formatted_address || cafe?.location_name;
      } else if (data.location_name?.location) {
        apiData.location = JSON.stringify(data.location_name?.location);
        apiData.location_name = data.location_name.formatted_address;
      } else if (cafe?.location) {
        // console.log("location---",cafe?.location)
        // apiData.location = cafe.location;
        // apiData.location_name = cafe.location_name;
      }
      console.log("apiData,", apiData);
      data.actionUrl = `cafe/${cafeId}`;
      data.actionMethod = "patch";
      data.apiData = apiData;
      dispatch(Entry(data)).then((resp) => {
        setSubmitting(false);
        if (resp.payload.code === 200) {
          toast.success("Cafe updated successfully");
          // Update the displayed image if a new one was uploaded
          if (selectedImageFile && resp.payload.data?.cafe?.image) {
            setImage(resp.payload.data.cafe.image);
          }
          resetImageSelection();
          handleClose();
          setIsUpdated(true);
          setAction("view");
        } else {
          console.log(resp,"ressssss");
          
          setError(resp.payload.message);
          toast.error("Error updating cafe");
        }
      });
    };

    const parseTime24 = (time) => {
      if (!time) return null;
      const [hours, minutes, seconds] = time.split(":").map(Number);
      return hours * 3600 + minutes * 60 + (seconds || 0);
    };

    const handleTimingsChange = (dayIndex, periodIndex, type, value) => {
      setTimings((prevTimings) => {
        const updatedTimings = [...prevTimings];
        const period = updatedTimings[dayIndex].periods[periodIndex];
        
        if (type === "startLocalTime") {
          period.startLocalTime = value || "";
        } else if (type === "endLocalTime") {
          period.endLocalTime = value || "";
        }

        // Validation
        if (period.startLocalTime && period.endLocalTime) {
          const startSeconds = parseTime24(period.startLocalTime);
          const endSeconds = parseTime24(period.endLocalTime);
          
          if (startSeconds === endSeconds) {
            toast.error("Start time and End time cannot be the same.");
            period[type] = "";
            setTimeInvalid(true);
            return updatedTimings;
          }

          // Check if end time is before start time (indicates next day)
          if (endSeconds < startSeconds) {
            period.isNextDay = true;
          } else {
            period.isNextDay = false;
          }
        }

        // Check if any period has incomplete times
        const hasInvalidPeriod = updatedTimings.some(timing =>
          timing.periods.some(p => 
            (p.startLocalTime && !p.endLocalTime) || 
            (!p.startLocalTime && p.endLocalTime)
          )
        );
        setTimeInvalid(hasInvalidPeriod);

        return updatedTimings;
      });
      return true;
    };

    const handleAddPeriod = (dayIndex) => {
      setTimings((prevTimings) => {
        const currentPeriods = prevTimings[dayIndex].periods;
        
        // Only validate if there are existing periods
        if (currentPeriods && currentPeriods.length > 0) {
          const hasInvalidPeriod = currentPeriods.some(p => 
            !p.startLocalTime || !p.endLocalTime
          );
          
          if (hasInvalidPeriod) {
            toast.error("Please complete the current period before adding a new one.");
            return prevTimings; // Don't add new period
          }
        }
        
        // Add new period with unique ID
        const updatedTimings = [...prevTimings];
        updatedTimings[dayIndex] = {
          ...updatedTimings[dayIndex],
          periods: [...updatedTimings[dayIndex].periods, {
            id: generatePeriodId(),
            startLocalTime: "",
            endLocalTime: "",
            isNextDay: false
          }]
        };
        return updatedTimings;
      });
    };

    const handleRemovePeriod = (dayIndex, periodId) => {
      setTimings((prevTimings) => {
        const updatedTimings = [...prevTimings];
        updatedTimings[dayIndex].periods = updatedTimings[dayIndex].periods.filter(
          period => period.id !== periodId
        );
        return updatedTimings;
      });
    };

    const getNextDayName = (day) => {
      const days = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];
      const index = days.indexOf(day.toLowerCase());
      return index !== -1 ? capitalize(days[(index + 1) % 7]) : "";
    };

    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

    const adjustTextareaHeight = (element) => {
      if (element) {
        element.style.height = "auto"; // Reset height to default
        element.style.height = `${Math.min(element.scrollHeight, 200)}px`; // Set new height with max limit of 200px
      }
    };

    const fetchAddons = async () => {
      try {
        setLoading(true);
        const addonResponse = await axios.get(
          `${envValues.REACT_APP_API_ENDPOINT}/cafe/cafe-addons/${cafeId}`,
          {
            method: "GET",
            headers: { Authorization: token },
          }
        );
        const addons = addonResponse.data?.data?.result?.addons;
        console.log("addons---", addons);
        setAddOnSets(addons);
        setUpdatedAddonSets(addons);
      } catch (error) {
        console.log("Error----", error);
      }
    };

    const fetchOptions = async () => {
      try {
        console.log("fetch options---");
        setLoading(true);
        const optionsResponse = await axios.get(
          `${envValues.REACT_APP_API_ENDPOINT}/cafe/cafe-options/${cafeId}`,
          {
            method: "GET",
            headers: { Authorization: token },
          }
        );
        const options = optionsResponse.data?.data?.result?.options;
        console.log("options---", options);
        setOptionsSets(options);
        setUpdatedOptions(options);
      } catch (error) {
        console.log("Error----", error);
      }
    };

    useEffect(() => {
      fetchAddons();
      fetchOptions();
      fetchNonMigratedRatingMenus();
      fetchAvailableDrinks();

      // Fetch cafe section approve permission
      const permissions = getPermissions();
      const cafeSection = "cafe";
      const vendorApplicationSection = "vendorapplications";
      permissions.forEach((val) => {
        if (val.section.replace(/\s+/g, "").toLowerCase() === cafeSection) {
          setCanApprove(val.approve ? val.approve : false);
        }
        if (val.section.replace(/\s+/g, "").toLowerCase() === vendorApplicationSection) {
          setCanEditVendorApplication(val.edit ? val.edit : false);
        }
      });
    }, []);

    const [isEditing, setIsEditing] = useState(null); // Track which addonSet is being edited
    const [modifierTagTarget, setModifierTagTarget] = useState(null);
    const [updatedAddonSets, setUpdatedAddonSets] = useState();

    const handleEditClick = (addonSetId) => {
      console.log("Addons set id--", addonSetId);
      setUpdatedAddonSets(addOnSets);
      setIsEditing(addonSetId);
    };

    const handleAddonSetNameChange = (setIndex, newName) => {
      const newAddonSets = [...updatedAddonSets];
      newAddonSets[setIndex].name = newName;
      setUpdatedAddonSets(newAddonSets);
    };

    const handleAddonNameChange = (setIndex, addonIndex, newName) => {
      const newAddonSets = [...updatedAddonSets];
      newAddonSets[setIndex].addOns[addonIndex].name = newName;
      setUpdatedAddonSets(newAddonSets);
    };

    const handleInputChange = (setId, addonIndex, newPrice) => {
      const newAddonSets = updatedAddonSets.map((set) =>
        set.id === setId || set._id === setId
          ? {
              ...set,
              addOns: set.addOns.map((addon, i) =>
                i === addonIndex
                  ? {
                      ...addon,
                      price: newPrice === "" ? "" : Number(newPrice),
                    }
                  : addon
              ),
            }
          : set
      );
      setUpdatedAddonSets(newAddonSets);
    };

    const handleAddAddon = (setIndex) => {
      const newAddonSets = [...updatedAddonSets];
      newAddonSets[setIndex].addOns.push({
        _id: Date.now().toString(),
        name: "New Addon",
        price: "",
      });
      setUpdatedAddonSets(newAddonSets);
    };

    const handleRemoveAddon = (setIndex, addonIndex) => {
      const newAddonSets = [...updatedAddonSets];
      newAddonSets[setIndex].addOns.splice(addonIndex, 1);
      setUpdatedAddonSets(newAddonSets);
    };

    const handleSave = async () => {
      const addonSetToUpdate = updatedAddonSets.find(
        (set) => (set.id || set._id) === isEditing
      );
      const orderAheadEnabled = isCafeOrderAheadEnabled(cafe);
      const invalidAddon = (addonSetToUpdate?.addOns || []).find((addon) =>
        addonPriceError(addon.price, orderAheadEnabled)
      );
      if (invalidAddon) {
        toast.error(addonPriceError(invalidAddon.price, orderAheadEnabled));
        return;
      }
      const payload = {
        ...addonSetToUpdate,
        addOns: (addonSetToUpdate?.addOns || []).map((addon) => ({
          ...addon,
          price: normalizeAddonPrice(addon.price),
        })),
      };
      try {
        const addonResponse = await axios.post(
          `${envValues.REACT_APP_API_ENDPOINT}/cafe/update-addons/${isEditing}`,
          {
            addonSetToUpdate: payload,
          },
          {
            headers: { Authorization: token },
          }
        );
        if (addonResponse.status === 200) {
          console.log("Updated successfully:", addonResponse.data);
          setIsEditing(null);
        }
        fetchAddons();
      } catch (err) {
        console.error("Error updating add-ons:", err);
        setError("Failed to update. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    const handleCancel = () => {
      setUpdatedAddonSets(addOnSets); // Reset values
      setIsEditing(null); // Exit edit mode
    };

    const handleDeleteAddon = async (addSetId) => {
      try {
        console.log("addon set id", addSetId);
        const addonResponse = await axios.delete(
          `${envValues.REACT_APP_API_ENDPOINT}/cafe/delete-addons/${addSetId}`,
          {
            headers: { Authorization: token },
          }
        );
        if (addonResponse.status === 200) {
          console.log("deleted successfully:", addonResponse.data);
        }
        fetchAddons();
      } catch (err) {
        console.error("Error updating add-ons:", err);
        setError("Failed to update. Please try again.");
      }
    };

    // ################ Options Section###################
    const [isEditingOption, setIsEditingOption] = useState(null); // Track which addonSet is being edited
    const [updatedOptions, setUpdatedOptions] = useState([]);
    const [nonMigratedDrinks, setNonMigratedDrinks] = useState([]);
    const [availableDrinks, setAvailableDrinks] = useState([]);

    const handleEditClickOption = (optionId) => {
      setIsEditingOption(optionId);
    };

    const handleDeleteClickOption = async (optionId) => {
      const optionsResponse = await axios.post(
        `${envValues.REACT_APP_API_ENDPOINT}/cafe/delete-options/${optionId}`,
        {},
        {
          headers: { Authorization: token },
        }
      );
      fetchOptions();
    };

    // Handle input change
    const handleInputChangeOption = (optionId, field, value) => {
      console.log("field value options=----", field, value);
      console.log("field value options=----", optionId);

      console.log("updated options=----", updatedOptions);
      setUpdatedOptions((prev) =>
        prev.map((option) =>
          option.id === optionId ? { ...option, [field]: value } : option
        )
      );
    };

    // Handle input change for value fields
    const handleInputChangeValue = (optionId, valueId, field, value) => {
      setUpdatedOptions((prev) =>
        prev.map((option) =>
          option.id === optionId
            ? {
                ...option,
                values: option.values.map((val) =>
                  val._id === valueId ? { ...val, [field]: value } : val
                ),
              }
            : option
        )
      );
    };

    const handleRemoveOptionValue = (optionId, valueId) => {
      setUpdatedOptions((prevOptions) =>
        prevOptions.map((option) =>
          option.id === optionId
            ? {
                ...option,
                values: option.values.filter((val) => val._id !== valueId),
              }
            : option
        )
      );
    };

    const [newOptionValues, setNewOptionValues] = useState({});

    // Function to add a new value to an existing option set
    const handleAddNewValue = (optionId) => {
      const newValue = newOptionValues[optionId]?.trim();
      if (!newValue) return;

      setUpdatedOptions((prevOptions) =>
        prevOptions.map((opt) =>
          opt.id === optionId
            ? {
                ...opt,
                values: [...opt.values, { name: newValue }],
              }
            : opt
        )
      );

      // Clear input field after adding
      setNewOptionValues((prev) => ({
        ...prev,
        [optionId]: "",
      }));
    };
    console.log("update options 123---", updatedOptions);

    // Save changes
    const handleSaveOption = async (optionId) => {
      try {
        console.log("update options---", updatedOptions);

        const optionSetToUpdate = Object.values(updatedOptions).find(
          (set) => (set.id || set._id) === optionId
        );

        console.log("update fields---", optionSetToUpdate);
        const optionsResponse = await axios.post(
          `${envValues.REACT_APP_API_ENDPOINT}/cafe/update-options/${isEditingOption}`,
          {
            optionSetToUpdate,
          },
          {
            headers: { Authorization: token },
          }
        );

        setIsEditingOption(null);
        fetchOptions(); // Refresh options after update
      } catch (error) {
        console.error("Error updating option:", error);
      }
    };

    // -----------Non migrated ratings------------
    const fetchNonMigratedRatingMenus = async () => {
      try {
        const drinks = await axios.get(
          `${envValues.REACT_APP_API_ENDPOINT}/cafe/non-migrated-rating-menus/${cafeId}`,
          {
            method: "GET",
            headers: { Authorization: token },
          }
        );
        console.log(
          "non migrated dirnks---",
          drinks?.data?.data?.result?.drinks
        );
        setNonMigratedDrinks(drinks?.data?.data?.result?.drinks);
      } catch (error) {
        console.log("Error----", error);
      }
    };

    const fetchAvailableDrinks = async () => {
      try {
        const drinks = await axios.get(
          `${envValues.REACT_APP_API_ENDPOINT}/cafe/available-drinks-to-migrate/${cafeId}`,
          {
            method: "GET",
            headers: { Authorization: token },
          }
        );
        console.log(
          "non migrated dirnks---",
          drinks?.data?.data?.result?.drinks
        );
        setAvailableDrinks(drinks?.data?.data?.result?.drinks);
      } catch (error) {}
    };

    const [selectedDrinkId, setSelectedDrinkId] = useState();

    const handleSelectionChange = (selectedId) => {
      setSelectedDrinkId(selectedId);
    };

    const handleDeleteMigration = async (drinkId) => {
      try {
        confirmAlert({
          title: "Confirm Deletion",
          message:
            "Are you sure you want to delete this drink without migration?",
          buttons: [
            {
              label: "Yes",
              onClick: () => {
                const data = {
                  actionUrl: `cafe/delete-migration/${drinkId}`,
                  actionMethod: "delete",
                };
                dispatch(Entry(data))
                  .then((resp) => {
                    if (resp.payload.code === 200) {
                      toast.success("Rating migration deleted successfully");
                      fetchNonMigratedRatingMenus();
                      fetchAvailableDrinks();
                    } else {
                      toast.error("Error deleting migration");
                    }
                  })
                  .catch((error) => {
                    toast.error("Error deleting migration. Please try again.");
                  });
              },
            },
            {
              label: "No",
            },
          ],
        });
      } catch (error) {}
    };

    const handleMigration = async (drinkId, selectedDrinkId) => {
      try {
        console.log("drinkIdss---", drinkId, selectedDrinkId);
        if (!drinkId || !selectedDrinkId) {
          toast.error("Please select a valid drink before migrating.");
          return;
        }
        await axios.post(
          `${envValues.REACT_APP_API_ENDPOINT}/cafe/migrate-rating/${drinkId}`,
          {
            selectedDrinkId,
          },
          {
            headers: { Authorization: token },
          }
        );

        toast.success("Rating migration completed successfully");
        fetchNonMigratedRatingMenus();
        fetchAvailableDrinks();
      } catch (error) {
        toast.error("Rating migration failed");
      }
    };

    //-------------Commission Model--------------------
    const commissions = [
      // { value: "Auto Enable", label: "Auto Enable" },
      { value: "Enable", label: "Enable" },
      { value: "Disable", label: "Disable" },
    ];

    const uptickValues = [
      { value: "Enable", label: "Enable" },
      { value: "Disable", label: "Disable" },
    ];

    const uptickTypes = [
      { value: "Percentage", label: "Percentage" },
      { value: "Amount", label: "Amount" },
    ];

    console.log("commission value=---", commissionValue);
    const selectedCommission = watch("commission");
    const selectedUptickValue = watch("uptickAmountStatus");
    const selectedUptickType = watch("uptickAmountType");
    const commissionMilestoneOverride = watch("commissionMilestoneOverride");

    console.log("selectedUptickValue--", selectedUptickValue);

    //-----------Duplicate cafe---------------------
    const duplicateCafe = async () => {
      confirmAlert({
        title: canDelete ? "" : "Oops!!",
        message:
          "Are you sure you want to duplicate this café along with its menu and settings?",
        buttons: canDelete
          ? [
              {
                label: "Yes",
                onClick: () => {
                  setDuplicateLoading(true);
                  const actionData = {};
                  actionData.actionUrl = `cafe/duplicate-cafe/${cafeId}`;
                  actionData.actionMethod = "post";
                  dispatch(Entry(actionData))
                    .then((data) => {
                      setDuplicateLoading(false);
                      setSelectedCafeId(data?.payload?.data?.result?.id);
                      setShowTablePopUp(true);
                      // // navigate(-1);
                      // handleClose();
                      setIsUpdated(true);
                      // toast.success("Duplicate cafe created successfully");
                    })
                    .catch((err) => {
                      setDuplicateLoading(false);
                      toast.error("Error creating duplicate cafe");
                      console.error("Error: ", err);
                    });
                },
              },
              {
                label: "No",
                // onClick: () => alert("Click No")
              },
            ]
          : [
              {
                label: "Ok",
                // onClick: () => alert("Click No")
              },
            ],
      });
    };

    //-----------Unlink cafe vendor---------------------
    const unLinkVendor = async () => {
      confirmAlert({
        title: canDelete ? "" : "Oops!!",
        message: "Are you sure you want to Disclaim this café?",
        buttons: canDelete
          ? [
              {
                label: "Yes",
                onClick: () => {
                  setDuplicateLoading(true);
                  const actionData = {};
                  actionData.actionUrl = `cafe/unlink-cafe-vendor/${cafeId}`;
                  actionData.actionMethod = "post";
                  dispatch(Entry(actionData))
                    .then((data) => {
                      setDuplicateLoading(false);
                      setSelectedCafeId(data?.payload?.data?.result?.id);
                      setShowTablePopUp(true);
                      // // navigate(-1);
                      // handleClose();
                      // setIsUpdated(true);
                      // toast.success("Duplicate cafe created successfully");
                    })
                    .catch((err) => {
                      setDuplicateLoading(false);
                      toast.error("Error creating duplicate cafe");
                      console.error("Error: ", err);
                    });
                },
              },
              {
                label: "No",
                // onClick: () => alert("Click No")
              },
            ]
          : [
              {
                label: "Ok",
                // onClick: () => alert("Click No")
              },
            ],
      });
    };

    const manageTax = async () => {
      navigate(`/tax/${cafeId}`);
    };

    const cafePhotoSrc = imagePreview || image;
    const showCafePhoto =
      isUsableCafeImageUrl(cafePhotoSrc) && !cafeImageLoadFailed;

    return (
      <div className={styles.popupbox}>
        {!duplicateLoading ? (
          <div className={styles.box}>
            <div className={styles.popupHeader}>
              <div className={styles.popupHeaderTopBar}>
                <h2 className={styles.popupHeaderPageTitle}>
                  {isEditMode ? "Edit Cafe Details" : "Cafe Details"}
                </h2>
                <button
                  className={styles.closeicon}
                  aria-label="Close"
                  onClick={handleCloseWithReset}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <div className={styles.popupHeaderContent}>
                <input
                  type="file"
                  ref={imageInputRef}
                  onChange={handleImageChange}
                  accept="image/*"
                  style={{ display: "none" }}
                  aria-label="Upload cafe image"
                />

                <div className={styles.popupHeaderMain}>
                {cafe && (
                  <div className={styles.popupHeaderAvatar}>
                    <CafeNameAvatar
                      name={cafe.name}
                      src={cafePhotoSrc}
                      size={100}
                      imageLoadFailed={cafeImageLoadFailed}
                      onImageError={() => setCafeImageLoadFailed(true)}
                      interactive={isEditMode}
                      onClick={handleImageClick}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleImageClick();
                        }
                      }}
                      ariaLabel={
                        isEditMode
                          ? showCafePhoto
                            ? "Click to change cafe image"
                            : "Click to add cafe image"
                          : `${cafe.name || "Cafe"} avatar`
                      }
                      overlay={
                        isEditMode ? <FontAwesomeIcon icon={faPen} /> : null
                      }
                    />
                  </div>
                )}
                <div className={styles.popupHeaderMeta}>
                  <div className={styles.popupHeaderTitleRow}>
                    <h2 className={styles.heading}>{cafe?.name || "Cafe"}</h2>
                    <OrderAheadEnabledBadge cafe={cafe} />
                  </div>
                  <div className={styles.popupHeaderActions}>
                {!isEditMode && edit && (
                  <button
                    className={styles.formbtnedit}
                    onClick={() => setIsEditMode(true)}
                    aria-label="Edit"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                    Edit
                  </button>
                )}
                {!isEditMode && edit && !cafe?.posConnected && (
                  <button
                    className={styles.formbtnedit}
                    onClick={() => duplicateCafe()}
                    aria-label="Duplicate cafe"
                  >
                    <i className="fa fa-copy me-1" aria-hidden="true" />
                    Duplicate Cafe
                  </button>
                )}

                {!isEditMode && edit && cafe?.vendor && (
                  <button
                    className={styles.formbtnedit}
                    onClick={() => unLinkVendor()}
                    aria-label="Disclaim cafe"
                  >
                    <i className="fa fa-unlink me-1" aria-hidden="true" />
                    Disclaim Cafe
                  </button>
                )}

                {!isEditMode && cafe?.posConnected && cafe?.posSoftware === "square" && (
                  <button
                    className={styles.formbtnedit}
                    onClick={() => setShowRefreshModal(true)}
                    aria-label="Sync from Square"
                    style={{ backgroundColor: "#17a2b8", color: "white" }}
                  >
                    <i className="fa fa-refresh me-1" aria-hidden="true" />
                    Sync from Square
                  </button>
                )}

                <button
                  className={styles.formbtnedit}
                  onClick={() => manageTax()}
                  aria-label="Manage tax"
                >
                  <i className="fa fa-percent me-1" aria-hidden="true" />
                  Manage Tax
                </button>
                  </div>
                </div>
                </div>
              </div>
            </div>

            <div className={styles.popupBody}>
              <div className="container-fluid editcafeform">
              {cafe ? (
                <form onSubmit={handleSubmit(onSubmitData)}>
                  <div className="row">
                    <div className="col-md-6 col-sm-6">
                      <TextInput
                        className={styles.inputbox}
                        label="Name"
                        fieldName="name"
                        placeHolder="Enter Cafe Name"
                        register={register}
                        errors={errors}
                        isRequired
                        mandatory
                        readOnly={!isEditMode}
                      />
                    </div>
                    <div className="col-lg-6 col-md-12 col-sm-12">
                      <label htmlFor="description" className={styles.formlabel}>
                        Description
                      </label>
                      <textarea
                        id="description"
                        className={`${styles.inputbox}`}
                        placeholder="Enter Description"
                        {...register("description")}
                        rows={1}
                        // readOnly={!isEditMode}
                        disabled={!isEditMode}
                        style={{ resize: "none", overflowY: "auto" }}
                        onInput={(e) => {
                          e.target.style.height = "auto";
                          e.target.style.height = `${Math.min(
                            e.target.scrollHeight,
                            80
                          )}px`;
                        }}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 col-sm-6">
                      <TextInput
                        className={styles.inputbox}
                        label="Address"
                        fieldName="address"
                        placeHolder="Enter Cafe Address"
                        register={register}
                        errors={errors}
                        maximLength={100}
                        isRequired
                        mandatory
                        readOnly={!isEditMode}
                      />
                    </div>
                    <div className="col-md-6 col-sm-6">
                      <div style={{ position: "relative" }}>
                        <LocationSelectBox
                          className={styles.inputbox}
                          classNamedark={styles.inputbox1}
                          name="location_name"
                          label="Location (For GPS) - Search or Pick on Map"
                          labelstyle={styles.formlabel}
                          control={control}
                          register={register}
                          values={locations}
                          errors={errors}
                          placeholder={text || "Search location or use map below"}
                          defaultValue={text}
                          disabled={!isEditMode}
                          mandatory={false}
                          isRequired={false}
                          setText
                          handleLocationChange={handleLocationChange}
                          onLocationSelect={(locationData) => {
                            console.log(
                              "Location selected in edit:",
                              locationData
                            );
                            setSelectedLocationData(locationData);
                            setManualCoordinates(null); // Reset manual pin when using autocomplete
                          }}
                          isLoading={locationLoading}
                        />
                        {isEditMode && (
                          <>
                            <button
                              type="button"
                              onClick={handleOpenMapModal}
                              style={{
                                marginTop: "8px",
                                padding: "8px 16px",
                                backgroundColor: "#39979d",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight: "600",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = "#2d7b80";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = "#39979d";
                              }}
                            >
                              📍 {manualCoordinates || watchLocationName?.location || cafe?.location ? "Adjust Pin" : "Pick Location on Map"}
                            </button>
                            {(manualCoordinates || watchLocationName?.location || cafe?.location) && (
                              <div
                                style={{
                                  marginTop: "10px",
                                  padding: "12px",
                                  backgroundColor: "#e8f5e9",
                                  borderRadius: "6px",
                                  border: "1px solid #81c784",
                                  fontSize: "13px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "8px",
                                  }}
                                >
                                  <strong style={{ color: "#2e7d32" }}>
                                    ✓ Location Selected
                                  </strong>
                                  {manualCoordinates && (
                                    <button
                                      type="button"
                                      onClick={handleResetManualPin}
                                      style={{
                                        padding: "4px 10px",
                                        fontSize: "12px",
                                        backgroundColor: "#fff",
                                        color: "#d32f2f",
                                        border: "1px solid #d32f2f",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        fontWeight: "500",
                                      }}
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>
                                <div style={{ color: "#424242", fontSize: "14px", lineHeight: "1.5" }}>
                                  {selectedLocationData?.formatted_address || watchLocationName?.formatted_address || cafe?.location_name || "Location coordinates set"}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                    <div className="row">
                    {!isEditMode && (  <div className="col-lg-6 col-md-6 col-sm-6">
                        <TextInput
                          className={styles.inputbox}
                          label="Phone Number"
                          fieldName="phone"
                          placeHolder="Enter Phone Number"
                          register={register}
                          errors={errors}
                          isRequired
                          mandatory
                          readOnly={!isEditMode}
                        />
                      </div>)}

                      {cafe?.cafeStatus === "Unclaimed" && (<div className="col-md-6 col-sm-6">
                        <EmailInput
                          className={styles.inputbox}
                          label="Email"
                          fieldName="email"
                          register={register}
                          errors={errors}
                          placeHolder="Email"
                          readOnly={!isEditMode}
                        />
                      </div>)}
                    </div>
                  <div className="row">
                    <div className="col-lg-6 col-md-12 col-sm-12">
                      <TextInput
                        className={styles.inputbox}
                        classNamedark={styles.inputbox1}
                        labelstyle={styles.formlabel}
                        label="Facebook"
                        placeHolder="Facebook"
                        fieldName="facebook"
                        register={register}
                        errors={errors}
                        readOnly={!isEditMode}

                        // isRequired
                        // mandatory
                      />
                    </div>
                    <div className="col-lg-6 col-md-12 col-sm-12">
                      {" "}
                      <TextInput
                        className={styles.inputbox}
                        classNamedark={styles.inputbox1}
                        labelstyle={styles.formlabel}
                        label="Instagram"
                        placeHolder="Instagram"
                        fieldName="instagram"
                        register={register}
                        errors={errors}
                        readOnly={!isEditMode}

                        // isRequired
                        // mandatory
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-6 col-md-12 col-sm-12">
                      <TextInput
                        className={styles.inputbox}
                        classNamedark={styles.inputbox1}
                        labelstyle={styles.formlabel}
                        label="Business Address"
                        placeHolder="Business Address"
                        fieldName="businessAddress"
                        register={register}
                        errors={errors}
                        readOnly={!isEditMode}

                        // isRequired
                        // mandatory
                      />
                    </div>
                    <div className="col-lg-6 col-md-12 col-sm-12">
                      {" "}
                      <TextInput
                        className={styles.inputbox}
                        classNamedark={styles.inputbox1}
                        labelstyle={styles.formlabel}
                        label="Location Title"
                        placeHolder="Enter location title (e.g. Downtown Seattle)"
                        fieldName="locationTitle"
                        register={register}
                        errors={errors}
                        readOnly={!isEditMode}

                        // isRequired
                        // mandatory
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-6 col-md-12 col-sm-12">
                      <SelectBox
                        className={styles.inputbox}
                        name="commission"
                        label="Commission model"
                        labelstyle={styles.formlabel}
                        control={control}
                        register={register}
                        values={commissions}
                        errors={errors}
                        placeholder="Select Commision Model"
                        mandatory
                        isRequired
                        defaultvalue={commissionValue}
                        Disabled={!isEditMode}
                      />
                    </div>
                    <div className="col-lg-6 col-md-12 col-sm-12">
                      {" "}
                      <NumberInput
                        className={styles.inputbox}
                        classNamedark={styles.inputbox1}
                        labelstyle={styles.formlabel}
                        label="Commission Amount"
                        placeHolder="Enter Commission Amount"
                        fieldName="commissionAmount"
                        register={register}
                        errors={errors}
                        // isRequired
                        readOnly={!isEditMode}
                        // mandatory
                      />
                    </div>
                    {selectedCommission !== "Disable" && (
                      <div className="col-lg-6 col-md-12 col-sm-12">
                        <div className="form-check mb-2">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="commissionMilestoneOverride"
                            {...register("commissionMilestoneOverride")}
                            disabled={!isEditMode}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="commissionMilestoneOverride"
                            style={{ fontSize: "14px" }}
                          >
                            Override Commission Item Count
                          </label>
                        </div>
                        {commissionMilestoneOverride && (
                          <NumberInput
                            className={styles.inputbox}
                            classNamedark={styles.inputbox1}
                            labelstyle={styles.formlabel}
                            label="Commission Milestone (Every N Items)"
                            placeHolder="e.g. 10"
                            fieldName="commissionMilestone"
                            register={register}
                            errors={errors}
                            readOnly={!isEditMode}
                          />
                        )}
                      </div>
                    )}

                    <div className="row">
                      <div className="col-md-3 col-sm-3">
                        <TextInput
                          className={styles.inputbox}
                          label="Credit Card Fee Percentage (%)"
                          fieldName="ccProcessingConfig.percentage"
                          placeHolder="e.g. 2.9"
                          register={register}
                          errors={errors}
                          // isRequired
                          // mandatory
                          readOnly={!isEditMode}
                        />
                      </div>
                      <div className="col-md-3 col-sm-3">
                        <TextInput
                          className={styles.inputbox}
                          label="Credit Card Fixed Fee ($)"
                          fieldName="ccProcessingConfig.fixedFee"
                          placeHolder="e.g. 0.3"
                          register={register}
                          errors={errors}
                          // isRequired
                          // mandatory
                          readOnly={!isEditMode}
                        />
                      </div>
                    </div>
                  </div>

                  <div className={`row ${styles.uptickSettingsRow}`}>
                    <div className="col-md-3 col-sm-3">
                      <SelectBox
                        className={styles.inputbox}
                        name="uptickAmountStatus"
                        label="Uptick Amount"
                        labelstyle={styles.formlabel}
                        control={control}
                        register={register}
                        values={uptickValues}
                        errors={errors}
                        placeholder="Select Uptick Model"
                        mandatory
                        isRequired
                        defaultvalue={uptickAmountStatus}
                        Disabled={!isEditMode}
                      />
                    </div>
                    <div className="col-md-3 col-sm-3 d-flex align-items-end pb-2">
                      <div className="form-check mb-0">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="hasSpecialInstruction"
                          {...register("hasSpecialInstruction")}
                          disabled={!isEditMode}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="hasSpecialInstruction"
                          style={{ fontSize: "14px" }}
                        >
                          Allow special instructions on orders
                        </label>
                      </div>
                    </div>
                    {selectedUptickValue === "Enable" && (
                      <div className="col-md-3 col-sm-3">
                        <SelectBox
                          className={styles.inputbox}
                          name="uptickAmountType"
                          label="Uptick Amount Type"
                          labelstyle={styles.formlabel}
                          control={control}
                          register={register}
                          values={uptickTypes}
                          errors={errors}
                          placeholder="Select Uptick Amount Type"
                          mandatory
                          isRequired
                          defaultvalue={uptickAmountType}
                          Disabled={!isEditMode}
                        />
                      </div>
                    )}
                    {selectedUptickValue === "Enable" && (
                      <div className="col-lg-6 col-md-12 col-sm-12">
                        <TextInput
                          className={styles.inputbox}
                          label={
                            selectedUptickType === "Percentage"
                              ? "Uptick Amount Percentage (%)"
                              : "Uptick Amount in Dollar ($)"
                          }
                          fieldName="uptickAmount"
                          placeHolder="e.g. 2.9"
                          register={register}
                          errors={errors}
                          // isRequired
                          // mandatory
                          readOnly={!isEditMode}
                        />
                      </div>
                    )}
                  </div>

                  <VendorDocumentsSection
                    cafe={cafe}
                    dateFormat={dateFormat}
                    canReviewDocuments={canEditVendorApplication}
                    onReviewDocument={(documentType) =>
                      handleReviewDocument(cafeId, documentType)
                    }
                  />

                  {edit && (
                    <div className={styles.cafeActionsSection}>
                    <Row className="g-3 align-items-end">
                      {cafe?.cafeStatus === "Pending" && canApprove && (
                        <>
                          <Col lg={2} md={4} sm={6}>
                            <div className={styles.empdiv}>
                              <Button
                                className={`${styles.title} btn-success`}
                                onClick={() => handleApprove(cafeId)}
                              >
                                <i className="fa fa-check me-1" aria-hidden="true" />
                                Approve Cafe
                              </Button>
                            </div>
                          </Col>
                          <Col lg={2} md={4} sm={6}>
                            <div className={styles.empdiv}>
                              <Button
                                className={`${styles.title} btn-danger`}
                                onClick={() => handleReject()}
                              >
                                <i className="fa fa-times me-1" aria-hidden="true" />
                                Reject Cafe
                              </Button>
                            </div>
                          </Col>
                        </>
                      )}

                      <Col lg={2} md={4} sm={6}>
                        <div className={styles.empdiv}>
                          {suspend ? (
                            <Button
                              className={styles.title}
                              onClick={() => removeSuspension(cafeId)}
                            >
                              <i className="fa fa-unlock me-1" aria-hidden="true" />
                              Remove Suspension
                            </Button>
                          ) : (
                            <Button
                              className={`${styles.title} btn-secondary`}
                              onClick={() => setShowModal(true)}
                            >
                              <i className="fa fa-ban me-1" aria-hidden="true" />
                              Suspend Cafe
                            </Button>
                          )}

                          <SuspendCafeModal
                            show={showModal}
                            handleClose={handleCloseModal}
                            cafeId={cafeId}
                          />
                          <RefreshPosItemsModal
                            show={showRefreshModal}
                            handleClose={handleCloseRefreshModal}
                            cafeId={cafeId}
                            cafeName={cafe?.name}
                          />
                        </div>
                      </Col>
                      <Col lg={2} md={4} sm={6}>
                        <div className={styles.empdiv}>
                          <Button
                            className={`${styles.title} btn-danger`}
                            onClick={() => {
                              handleDelete(cafeId);
                            }}
                          >
                            <i className="fa fa-trash me-1" aria-hidden="true" />
                            Delete Cafe
                          </Button>
                        </div>
                      </Col>
                      {!details?.isClaimed && !details.isClaimed && (
                        <Col lg={4} md={6} sm={12}>
                          <div className={styles.empdiv}>
                            <Button
                              className={`${styles.title} btn-primary`}
                              disabled={details?.claimRequests?.length === 0}
                              onClick={() => navigate(`/claimrequests`)}
                            >
                              <i className="fa fa-flag me-1" aria-hidden="true" />
                              Claim Requests: {claimRequestCount}
                            </Button>
                          </div>
                        </Col>
                      )}
                    </Row>
                    </div>
                  )}

                  {isEditMode && (
                    <div
                      className={`col-md-4 col-lg-4 col-sm-6 ${styles.fileupload}`}
                      style={{ margin: "10px" }}
                    >
                      <MultipleFileUpload
                        section="cafe"
                        disabled={!isEditMode}
                        register={register}
                        errors={errors}
                      />
                      {/* <MultipleFileUpload section="menus" disabled={!isEditMode} /> */}
                      {error && <h6 className={styles.errormsg}>{error}</h6>}
                    </div>
                  )}

                  {isEditMode && timeInvalid && (
                    <span className={styles.errormsg}>
                      Please ensure all cafe timings are valid. Complete any
                      missing <strong>start time</strong> or{" "}
                      <strong>end time</strong> for all periods.
                    </span>
                  )}

                  <div className="col-md-8 col-lg-8 col-sm-6">
                    <h6 className={styles.headinghours}>Cafe Hours (24-hour format)</h6>
                    {timings.map((timing, dayIndex) => (
                      <div key={timing.dayOfWeek} className="mb-3" style={{ border: "1px solid #e0e0e0", padding: "15px", borderRadius: "8px" }}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0" style={{ fontWeight: "600", color: "#333" }}>
                            {timing.dayOfWeek}
                          </h6>
                          {isEditMode && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleAddPeriod(dayIndex);
                              }}
                              className="btn btn-sm"
                              style={{ 
                                backgroundColor: "#39979d", 
                                color: "white", 
                                padding: "5px 15px",
                                fontSize: "13px",
                                border: "none",
                                borderRadius: "4px"
                              }}
                            >
                              + Add Period
                            </button>
                          )}
                        </div>
                        
                        {timing.periods.length === 0 ? (
                          <div style={{ textAlign: "center", color: "#999", padding: "20px 10px", fontSize: "13px" }}>
                            No hours set
                          </div>
                        ) : (
                          timing.periods.map((period, periodIndex) => (
                            <Container key={period.id} className="mb-2">
                              <Row className="align-items-center">
                                <Col md={1} className="text-center">
                                  <small style={{ color: "#666" }}>Period {periodIndex + 1}</small>
                                </Col>
                                <Col md={4} className="text-center">
                                  <TimePickerComponent
                                    control={control}
                                    name={`timings[${dayIndex}].periods[${periodIndex}].startLocalTime`}
                                    timings={timings}
                                    index={dayIndex}
                                    type={"startLocalTime"}
                                    errors={errors}
                                    onTimeChange={(value) =>
                                      handleTimingsChange(dayIndex, periodIndex, "startLocalTime", value)
                                    }
                                    readOnly={!isEditMode}
                                  />
                                </Col>
                                <Col md={1} className="text-center">
                                  <span style={{ fontWeight: "600" }}>-</span>
                                </Col>
                                <Col md={4} className="text-center">
                                  <TimePickerComponent
                                    control={control}
                                    name={`timings[${dayIndex}].periods[${periodIndex}].endLocalTime`}
                                    timings={timings}
                                    index={dayIndex}
                                    type={"endLocalTime"}
                                    errors={errors}
                                    onTimeChange={(value) =>
                                      handleTimingsChange(dayIndex, periodIndex, "endLocalTime", value)
                                    }
                                    readOnly={!isEditMode}
                                  />
                                  {period.isNextDay && (
                                    <div style={{ fontSize: "0.75rem", color: "#ff6b6b", marginTop: "4px" }}>
                                      Closes next day
                                    </div>
                                  )}
                                </Col>
                                <Col md={2} className="text-center">
                                  {isEditMode && (
                                    <button
                                      type="button"
                                      onClick={() => handleRemovePeriod(dayIndex, period.id)}
                                      className="btn btn-sm btn-danger"
                                      style={{ padding: "5px 10px", fontSize: "12px" }}
                                    >
                                      Remove
                                    </button>
                                  )}
                                </Col>
                              </Row>
                            </Container>
                          ))
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Update Button at the Bottom */}
                  {isEditMode && (
                    <div className="row mt-4">
                      <div className="col text-center">
                        <input
                          className={`${styles.formbtn} btn-sm`}
                          type="submit"
                          style={{ marginBottom: "100px" }}
                          disabled={submitting || timeInvalid}
                          value={submitting ? "Please wait.." : "Update"}
                        />
                      </div>
                    </div>
                  )}
                </form>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    marginTop: "130px",
                    color: "#39979d",
                  }}
                >
                  <Loader />
                </div>
              )}

              <div className="row" style={{ marginTop: "40px" }}>
                <div className="col-md-6 col-lg-6 col-sm-6">
                  <h4 className={styles.headinghours}>AddOns</h4>
                  {!cafe?.posConnected && edit && (
                    <button
                      onClick={() => setShowAddOnModal(true)}
                      style={{
                        backgroundColor: "#28a745",
                        color: "#fff",
                        border: "none",
                        padding: "8px 12px",
                        cursor: "pointer",
                        borderRadius: "5px",
                        marginBottom: "10px",
                      }}
                    >
                      + Add Addon
                    </button>
                  )}

                  <AddOnModal
                    show={showAddOnModal}
                    handleClose={handleCloseAddOnModal}
                    cafeId={cafeId}
                    fetchAddons={fetchAddons}
                    orderAheadEnabled={isCafeOrderAheadEnabled(cafe)}
                  />

                  <div
                    style={{
                      maxHeight: "350px",
                      overflowY: "auto",
                      border: "1px solid #ccc",
                      padding: "10px",
                      borderRadius: "5px",
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    {updatedAddonSets?.length > 0 ? (
                      updatedAddonSets?.map((addonSet, setIndex) => (
                        <div
                          key={addonSet._id}
                          style={{ marginBottom: "15px" }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            {isEditing === (addonSet?.id || addonSet?._id) ? (
                              <input
                                type="text"
                                value={addonSet.name}
                                onChange={(e) =>
                                  handleAddonSetNameChange(
                                    setIndex,
                                    e.target.value
                                  )
                                }
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  marginBottom: "5px",
                                  border: "1px solid #ccc",
                                  borderRadius: "3px",
                                  padding: "5px",
                                }}
                              />
                            ) : (
                              <h5
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  marginBottom: "5px",
                                }}
                              >
                                {addonSet.name}
                              </h5>
                            )}

                            {cafe?.application === "Baristi" ||
                            addonSet?.application === "Baristi" ? (
                              isEditing === (addonSet?.id || addonSet?._id) ? (
                                <>
                                  <button
                                    onClick={handleSave}
                                    style={{
                                      backgroundColor: "#28a745",
                                      color: "#fff",
                                      border: "none",
                                      padding: "5px 10px",
                                      cursor: "pointer",
                                      borderRadius: "3px",
                                      marginRight: "5px",
                                    }}
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancel}
                                    style={{
                                      backgroundColor: "#dc3545",
                                      color: "#fff",
                                      border: "none",
                                      padding: "5px 10px",
                                      cursor: "pointer",
                                      borderRadius: "3px",
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                !cafe?.posConnected &&
                                edit && (
                                  <>
                                    <button
                                      onClick={() =>
                                        handleEditClick(
                                          addonSet?.id || addonSet?._id
                                        )
                                      }
                                      style={{
                                        backgroundColor: "#007bff",
                                        color: "#fff",
                                        border: "none",
                                        padding: "5px 10px",
                                        cursor: "pointer",
                                        borderRadius: "3px",
                                      }}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleDeleteAddon(
                                          addonSet?.id || addonSet?._id
                                        )
                                      }
                                      style={{
                                        backgroundColor: "red",
                                        color: "#fff",
                                        border: "none",
                                        padding: "5px 10px",
                                        cursor: "pointer",
                                        borderRadius: "3px",
                                      }}
                                    >
                                      Delete
                                    </button>
                                  </>
                                )
                              )
                            ) : null}
                          </div>

                          {/* Addons List */}
                          <ul
                            style={{
                              listStyleType: "none",
                              paddingLeft: "10px",
                              margin: "0",
                            }}
                          >
                            {addonSet.addOns.map((addon, index) => (
                              <li
                                key={addon._id}
                                style={{
                                  fontSize: "14px",
                                  padding: "5px 0",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {isEditing ===
                                (addonSet?.id || addonSet?._id) ? (
                                  <>
                                    <input
                                      type="text"
                                      value={addon.name}
                                      onChange={(e) =>
                                        handleAddonNameChange(
                                          setIndex,
                                          index,
                                          e.target.value
                                        )
                                      }
                                      style={{
                                        width: "120px",
                                        padding: "5px",
                                        marginRight: "5px",
                                        border: "1px solid #ccc",
                                        borderRadius: "3px",
                                      }}
                                    />
                                    <input
                                      type="number"
                                      placeholder={
                                        isCafeOrderAheadEnabled(cafe)
                                          ? "Price"
                                          : "Optional"
                                      }
                                      value={addon.price ?? ""}
                                      onChange={(e) =>
                                        handleInputChange(
                                          addonSet.id || addonSet?._id,
                                          index,
                                          e.target.value
                                        )
                                      }
                                      style={{
                                        width: "70px",
                                        padding: "5px",
                                        marginLeft: "5px",
                                        border: "1px solid #ccc",
                                        borderRadius: "3px",
                                      }}
                                    />
                                    <button
                                      onClick={() =>
                                        handleRemoveAddon(setIndex, index)
                                      }
                                      style={{
                                        backgroundColor: "#dc3545",
                                        color: "#fff",
                                        border: "none",
                                        padding: "5px",
                                        cursor: "pointer",
                                        borderRadius: "3px",
                                        marginLeft: "5px",
                                      }}
                                    >
                                      Remove
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <span style={{ flex: 1 }}>
                                      {addon.name}
                                      {addon.price !== null &&
                                      addon.price !== undefined &&
                                      addon.price !== ""
                                        ? ` - $${addon.price}`
                                        : ""}
                                    </span>
                                    {edit && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setModifierTagTarget({
                                            addonId: String(
                                              addonSet?.id || addonSet?._id
                                            ),
                                            modifierKey: String(
                                              addon.modifierId || addon._id || ""
                                            ),
                                            name: addon.name,
                                            groupName:
                                              addonSet.displayName ||
                                              addonSet.name,
                                          })
                                        }
                                        style={{
                                          backgroundColor: "#39979d",
                                          color: "#fff",
                                          border: "none",
                                          padding: "4px 8px",
                                          cursor: "pointer",
                                          borderRadius: "3px",
                                          marginLeft: "8px",
                                          fontSize: "12px",
                                        }}
                                        aria-label={`Assign identity tags to ${addon.name}`}
                                      >
                                        Identity Tags
                                      </button>
                                    )}
                                  </>
                                )}
                              </li>
                            ))}
                          </ul>

                          {/* Add New Addon */}
                          {isEditing === (addonSet?.id || addonSet?._id) &&
                            !cafe?.posConnected &&
                            edit && (
                              <button
                                onClick={() => handleAddAddon(setIndex)}
                                style={{
                                  backgroundColor: "#28a745",
                                  color: "#fff",
                                  border: "none",
                                  padding: "5px 10px",
                                  cursor: "pointer",
                                  borderRadius: "3px",
                                  marginTop: "5px",
                                }}
                              >
                                Add Addon
                              </button>
                            )}
                        </div>
                      ))
                    ) : (
                      <p style={{ fontSize: "14px", color: "#777" }}>
                        No AddOns available
                      </p>
                    )}
                  </div>
                </div>
                {modifierTagTarget && (
                  <div
                    className={ditStyles.modalBackdrop}
                    role="presentation"
                    onClick={() => setModifierTagTarget(null)}
                  >
                    <div
                      className={ditStyles.modal}
                      role="dialog"
                      aria-modal="true"
                      aria-label={`Assign identity tags to ${modifierTagTarget.name}`}
                      onClick={(ev) => ev.stopPropagation()}
                      onKeyDown={(ev) => {
                        if (ev.key === "Escape") setModifierTagTarget(null);
                      }}
                    >
                      <h2 className={ditStyles.modalTitle}>
                        Identity Tags — {modifierTagTarget.name}
                      </h2>
                      <p className={ditStyles.empty} style={{ marginBottom: 12 }}>
                        Group: {modifierTagTarget.groupName || "—"}
                      </p>
                      <div className={ditStyles.note}>
                        Tags apply only when a customer selects this modifier.
                      </div>
                      <DrinkIdentityTagAssigner
                        mode="modifier"
                        addonId={modifierTagTarget.addonId}
                        modifierKey={modifierTagTarget.modifierKey}
                        onSaved={() => setModifierTagTarget(null)}
                      />
                      <div className={ditStyles.modalActions}>
                        <button
                          type="button"
                          className={ditStyles.secondaryBtn}
                          onClick={() => setModifierTagTarget(null)}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                <div className="col-md-6 col-lg-6 col-sm-6">
                  <h4 className={styles.headinghours}>Options</h4>{" "}
                  {!cafe?.posConnected && edit && (
                    <button
                      onClick={() => setShowOptionsModal(true)}
                      style={{
                        backgroundColor: "#28a745",
                        color: "#fff",
                        border: "none",
                        padding: "8px 12px",
                        cursor: "pointer",
                        borderRadius: "5px",
                        marginBottom: "10px",
                      }}
                    >
                      + Add Options
                    </button>
                  )}
                  <OptionsModal
                    show={showOptionsModal}
                    handleClose={handleCloseOptionsModal}
                    cafeId={cafeId}
                    fetchOptions={fetchOptions}
                  />
                  <div
                    style={{
                      maxHeight: "350px",
                      overflowY: "auto",
                      border: "1px solid #ccc",
                      padding: "10px",
                      borderRadius: "5px",
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    {optionsSets.length > 0 ? (
                      optionsSets.map((option) => {
                        // Find the updated option from state
                        const updatedOption =
                          updatedOptions.find((opt) => opt.id === option.id) ||
                          option;

                        return (
                          <div key={option.id} style={{ marginBottom: "15px" }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <h5
                                style={{ fontSize: "16px", fontWeight: "bold" }}
                              >
                                {isEditingOption === option.id ? (
                                  <input
                                    type="text"
                                    value={updatedOption.name}
                                    onChange={(e) =>
                                      handleInputChangeOption(
                                        option.id,
                                        "name",
                                        e.target.value
                                      )
                                    }
                                    style={{
                                      padding: "5px",
                                      border: "1px solid #ccc",
                                      borderRadius: "3px",
                                    }}
                                  />
                                ) : (
                                  updatedOption.name
                                )}
                              </h5>

                              {isEditingOption === option.id ? (
                                <>
                                  <button
                                    onClick={() => handleSaveOption(option.id)}
                                    style={{
                                      backgroundColor: "#28a745",
                                      color: "#fff",
                                      border: "none",
                                      padding: "5px 10px",
                                      cursor: "pointer",
                                      borderRadius: "3px",
                                      marginRight: "5px",
                                    }}
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setIsEditingOption(null)}
                                    style={{
                                      backgroundColor: "#dc3545",
                                      color: "#fff",
                                      border: "none",
                                      padding: "5px 10px",
                                      cursor: "pointer",
                                      borderRadius: "3px",
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                !cafe?.posConnected &&
                                edit && (
                                  <button
                                    onClick={() =>
                                      handleEditClickOption(option.id)
                                    }
                                    style={{
                                      backgroundColor: "#007bff",
                                      color: "#fff",
                                      border: "none",
                                      padding: "5px 10px",
                                      cursor: "pointer",
                                      borderRadius: "3px",
                                    }}
                                  >
                                    Edit
                                  </button>
                                )
                              )}

                              {!cafe?.posConnected && edit && (
                                <button
                                  onClick={() =>
                                    handleDeleteClickOption(option.id)
                                  }
                                  style={{
                                    backgroundColor: "red",
                                    color: "#fff",
                                    border: "none",
                                    padding: "5px 10px",
                                    cursor: "pointer",
                                    borderRadius: "3px",
                                  }}
                                >
                                  Delete
                                </button>
                              )}
                            </div>

                            {/* Show Values inside each Option */}
                            {(updatedOption.values || []).length > 0 && (
                              <ul
                                style={{
                                  marginTop: "10px",
                                  paddingLeft: "15px",
                                }}
                              >
                                {(updatedOption.values || []).map(
                                  (value, index) => {
                                    const updatedValue =
                                      updatedOption.values.find(
                                        (val) => val._id === value._id
                                      ) || value;

                                    return (
                                      <li
                                        key={value._id}
                                        style={{
                                          fontSize: "14px",
                                          color: "#333",
                                        }}
                                      >
                                        {isEditingOption === option.id ? (
                                          <>
                                            <input
                                              type="text"
                                              value={updatedValue.name}
                                              onChange={(e) =>
                                                handleInputChangeValue(
                                                  option.id,
                                                  value._id,
                                                  "name",
                                                  e.target.value
                                                )
                                              }
                                              style={{
                                                padding: "5px",
                                                border: "1px solid #ccc",
                                                borderRadius: "3px",
                                              }}
                                            />
                                            <button
                                              onClick={() =>
                                                handleRemoveOptionValue(
                                                  option.id,
                                                  value._id
                                                )
                                              }
                                              style={{
                                                backgroundColor: "#dc3545",
                                                color: "#fff",
                                                border: "none",
                                                padding: "5px",
                                                cursor: "pointer",
                                                borderRadius: "3px",
                                                marginLeft: "5px",
                                              }}
                                            >
                                              Remove
                                            </button>
                                          </>
                                        ) : (
                                          updatedValue.name
                                        )}
                                      </li>
                                    );
                                  }
                                )}
                              </ul>
                            )}
                            {isEditingOption === option.id && (
                              <div
                                style={{
                                  marginTop: "10px",
                                  display: "flex",
                                  gap: "10px",
                                  alignItems: "center",
                                }}
                              >
                                <input
                                  type="text"
                                  placeholder="New Option Name"
                                  value={newOptionValues[option.id] || ""}
                                  onChange={(e) =>
                                    setNewOptionValues((prev) => ({
                                      ...prev,
                                      [option.id]: e.target.value,
                                    }))
                                  }
                                  style={{
                                    padding: "5px",
                                    border: "1px solid #ccc",
                                    borderRadius: "3px",
                                    flex: "1",
                                  }}
                                />
                                <button
                                  onClick={() => handleAddNewValue(option.id)}
                                  style={{
                                    backgroundColor: "#28a745",
                                    color: "#fff",
                                    border: "none",
                                    padding: "5px 10px",
                                    cursor: "pointer",
                                    borderRadius: "3px",
                                  }}
                                >
                                  + Add
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <p style={{ fontSize: "14px", color: "#777" }}>
                        No options available
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <Row>
              <h3 className={styles.heading}>Menu</h3>
            </Row>

            <Row>
              <Col lg={12}>
                <CustomTable
                  urlParam={`${REACT_APP_API_FETCH_MENUS}/${cafeId}?`}
                  viewPopUp={true}
                  paramId={cafeId}
                  section="Menus"
                  toggleButton={{ show: true, field: "active" }}
                  deleteCondition={{
                    checkCondition: false,
                    apiUrl: "www.google.com",
                  }}
                  posConnected={cafe?.posConnected}
                  edit={edit}
                />
              </Col>
            </Row>

            {cafe?.vendor && (
               <Row className="mt-4">
                  <Col lg={12}>
                     <h3 className={styles.heading}>Vendor Details</h3>
                     <div 
                        style={{ padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #e0e0e0', cursor: 'pointer', transition: 'all 0.2s ease' }}
                        onClick={() => setShowVendorPopup(true)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eef7f8'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9f9f9'}
                     >
                        <h4 style={{ margin: 0, color: '#39979d', fontWeight: '200',fontSize: '20px' }}>
                           {cafe.vendor.name || "Vendor Name"}
                        </h4>
                        <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666' }}>
                           Click to view full vendor details
                        </p>
                     </div>
                  </Col>
               </Row>
            )}
            
            {showVendorPopup && cafe?.vendor && (
               <ViewVendorPopup 
                  vendorId={cafe.vendor._id || cafe.vendor.id} 
                  handleClose={() => setShowVendorPopup(false)}
                  showDelete={false}
                  showCafes={false}
               />
            )}

            {/* Searchable Points Section */}
            {(cafe?.roasterFeatured || cafe?.espressoMachineModel || cafe?.altMilkOptions || 
              cafe?.altMilkBrands || cafe?.organicMenu || cafe?.fairTradeCertifications || 
              cafe?.ownershipHeights) && (
               <Row className="mt-4">
                  <Col lg={12}>
                     <h3 className={styles.heading}>Searchable Points</h3>
                     <div style={{ padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                        <Row>
                           {cafe?.roasterFeatured && (
                              <Col md={6} className="mb-3">
                                 <strong style={{ color: '#39979d' }}>Roaster Featured:</strong>
                                 <p style={{ margin: '5px 0 0 0', color: '#333' }}>{cafe.roasterFeatured}</p>
                              </Col>
                           )}
                           {cafe?.espressoMachineModel && (
                              <Col md={6} className="mb-3">
                                 <strong style={{ color: '#39979d' }}>Espresso Machine Model:</strong>
                                 <p style={{ margin: '5px 0 0 0', color: '#333' }}>{cafe.espressoMachineModel}</p>
                              </Col>
                           )}
                           {cafe?.altMilkOptions && (
                              <Col md={6} className="mb-3">
                                 <strong style={{ color: '#39979d' }}>Alt Milk Options:</strong>
                                 <p style={{ margin: '5px 0 0 0', color: '#333' }}>
                                    {Array.isArray(cafe.altMilkOptions) ? cafe.altMilkOptions.join(", ") : cafe.altMilkOptions}
                                 </p>
                              </Col>
                           )}
                           {cafe?.altMilkBrands && (
                              <Col md={6} className="mb-3">
                                 <strong style={{ color: '#39979d' }}>Alt Milk Brands:</strong>
                                 <p style={{ margin: '5px 0 0 0', color: '#333' }}>
                                    {Array.isArray(cafe.altMilkBrands) ? cafe.altMilkBrands.join(", ") : cafe.altMilkBrands}
                                 </p>
                              </Col>
                           )}
                           {cafe?.organicMenu !== undefined && (
                              <Col md={6} className="mb-3">
                                 <strong style={{ color: '#39979d' }}>Organic Menu:</strong>
                                 <p style={{ margin: '5px 0 0 0', color: '#333' }}>{cafe.organicMenu ? "Yes" : "No"}</p>
                              </Col>
                           )}
                           {cafe?.fairTradeCertifications && (
                              <Col md={6} className="mb-3">
                                 <strong style={{ color: '#39979d' }}>Fair Trade Certifications:</strong>
                                 <p style={{ margin: '5px 0 0 0', color: '#333' }}>
                                    {Array.isArray(cafe.fairTradeCertifications) ? cafe.fairTradeCertifications.join(", ") : cafe.fairTradeCertifications}
                                 </p>
                              </Col>
                           )}
                           {cafe?.ownershipHeights && (
                              <Col md={6} className="mb-3">
                                 <strong style={{ color: '#39979d' }}>Ownership Heights:</strong>
                                 <p style={{ margin: '5px 0 0 0', color: '#333' }}>
                                    {Array.isArray(cafe.ownershipHeights) ? cafe.ownershipHeights.join(", ") : cafe.ownershipHeights}
                                 </p>
                              </Col>
                           )}
                        </Row>
                     </div>
                  </Col>
               </Row>
            )}
            {nonMigratedDrinks?.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  padding: "10px",
                }}
              >
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {nonMigratedDrinks?.length > 0 && (
                    <div
                      style={{
                        padding: "15px",
                        maxWidth: "600px",
                        margin: "auto",
                        background: "#f9f9f9",
                        borderRadius: "8px",
                        boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: "20px",
                          fontWeight: "bold",
                          textAlign: "center",
                          marginBottom: "15px",
                        }}
                      >
                        Non Migrated Ratings
                      </h3>
                      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                        {nonMigratedDrinks.map((drink) => (
                          <li
                            key={drink._id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "10px",
                              borderBottom: "1px solid #ddd",
                              background: "#fff",
                              borderRadius: "5px",
                              marginBottom: "8px",
                            }}
                          >
                            <span style={{ flex: 2, fontWeight: "500" }}>
                              {drink.name} -{" "}
                              <span style={{ color: "#555" }}>
                                Ratings: {drink?.totalRating}
                              </span>
                            </span>

                            <select
                              value={selectedDrinkId || ""}
                              onChange={(e) =>
                                handleSelectionChange(e.target.value)
                              }
                              style={{
                                padding: "6px",
                                borderRadius: "4px",
                                border: "1px solid #ccc",
                                flex: 1,
                                margin: "0 10px",
                              }}
                            >
                              <option value="" disabled>
                                Select a drink
                              </option>
                              {availableDrinks.map((option) => (
                                <option
                                  key={option?._id || option?.id}
                                  value={option?._id || option?.id}
                                >
                                  {option.name}
                                </option>
                              ))}
                            </select>

                            <button
                              onClick={() =>
                                handleMigration(
                                  drink._id || drink.id,
                                  selectedDrinkId
                                )
                              }
                              disabled={!selectedDrinkId}
                              style={{
                                backgroundColor: selectedDrinkId
                                  ? "green"
                                  : "gray",
                                color: "white",
                                padding: "6px 12px",
                                border: "none",
                                cursor: selectedDrinkId
                                  ? "pointer"
                                  : "not-allowed",
                                borderRadius: "4px",
                                fontSize: "14px",
                                marginRight: "5px",
                              }}
                            >
                              Migrate
                            </button>

                            <button
                              onClick={() =>
                                handleDeleteMigration(drink?._id || drink?.id)
                              }
                              style={{
                                backgroundColor: "red",
                                color: "white",
                                padding: "6px 12px",
                                border: "none",
                                cursor: "pointer",
                                borderRadius: "4px",
                                fontSize: "14px",
                              }}
                            >
                              Delete
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </ul>
              </div>
            )}

            {cafe?.cafeParticipationRequests &&
              cafe?.cafeParticipationRequests?.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    padding: "10px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      textAlign: "center",
                      marginBottom: "15px",
                    }}
                  >
                    Cafe Participation Requests
                  </h3>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      textAlign: "left",
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            borderBottom: "1px solid #ccc",
                            padding: "8px",
                          }}
                        >
                          Name
                        </th>
                        <th
                          style={{
                            borderBottom: "1px solid #ccc",
                            padding: "8px",
                          }}
                        >
                          Email
                        </th>
                        <th
                          style={{
                            borderBottom: "1px solid #ccc",
                            padding: "8px",
                          }}
                        >
                          Requested Date
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {cafe.cafeParticipationRequests?.map((req, index) => (
                        <tr key={index}>
                          <td style={{ padding: "8px" }}>
                            {req?.user?.name || "-"}
                          </td>
                          <td style={{ padding: "8px" }}>
                            {req?.user?.email || "-"}
                          </td>
                          <td style={{ padding: "8px" }}>
                            {new Date(req?.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              marginTop: "130px",
              color: "#39979d",
            }}
          >
            <Loader />
          </div>
        )}

        {showTablePopUp && selectedCafeId && (
          <EditCafePopup
            setTableData={setTableData}
            cafeId={selectedCafeId}
            handleClose={setShowTablePopUp}
            setIsUpdated={setIsUpdated}
          />
        )}

        {/* Map Pin Picker Modal */}
        <MapPinPickerModal
          isOpen={showMapModal}
          onClose={() => setShowMapModal(false)}
          onConfirm={handleMapConfirm}
          initialCoordinates={
            manualCoordinates ||
            watchLocationName?.location ||
            selectedLocationData?.location ||
            (cafe?.location && typeof cafe.location === 'object' && cafe.location.type === 'Point' && Array.isArray(cafe.location.coordinates)
              ? { lng: cafe.location.coordinates[0], lat: cafe.location.coordinates[1] }
              : cafe?.location && typeof cafe.location === 'string'
              ? JSON.parse(cafe.location)
              : cafe?.location)
          }
          initialAddress={
            watchLocationName?.formatted_address ||
            cafe?.location_name ||
            ""
          }
          title="Set Precise Café Location"
        />
      </div>
    );
  }
);

export default EditCafePopup;
